import type {IssueFieldSelection, IssueFieldSelectionValues} from '@github-ui/issue-fields-shared/issue-field'
import {toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {
  getIssueFieldSelectionValue,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
  toIssueFieldWithValue,
} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {IssueField as IssueFieldPickerField} from '@github-ui/item-picker/IssueFieldPicker'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {useCallback, useMemo, useState} from 'react'

import type {ScopedField} from '../../utils/option-config'

type PinnedFieldsFromType = IssueTypePickerIssueType['pinnedFields']

export type UseIssueFieldPickersProps = {
  pinnedFieldsFromType: PinnedFieldsFromType
  setIssueFieldsWithValues: (fields: IssueFieldWithUnsavedValue[]) => void
  scopedFields: ScopedField[]
}

export function useIssueFieldPickers({
  pinnedFieldsFromType,
  setIssueFieldsWithValues,
  scopedFields,
}: UseIssueFieldPickersProps) {
  const [issueFields, setIssueFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [pendingSelectedField, setPendingSelectedField] = useState<IssueFieldSelection | null>(null)

  const selectedPinnedFields = useMemo(() => {
    return (
      pinnedFieldsFromType?.map(field => {
        const f = field as unknown as IssueFieldSelection
        return {
          id: f.id,
          name: f.name,
          dataType: f.dataType,
        }
      }) || []
    )
  }, [pinnedFieldsFromType])

  // Updates this hooks state and also the context's issue fields state (those with values)
  const updateIssueFields = useCallback(
    (fields: IssueFieldWithUnsavedValue[]) => {
      setIssueFields(fields)
      const setFields = []
      for (const field of fields) {
        if (field.value) {
          setFields.push(field)
        }
      }
      setIssueFieldsWithValues(setFields)
    },
    [setIssueFieldsWithValues],
  )

  const onPendingFieldCommit = useCallback(
    (fieldId: string, fieldValue: IssueFieldSelectionValues) => {
      if (!pendingSelectedField || pendingSelectedField.id !== fieldId) return

      // Remove from pending state
      setPendingSelectedField(null)

      const fieldType = toIssueFieldType(pendingSelectedField.dataType)
      const value = getIssueFieldSelectionValue(fieldType, fieldValue)
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // If value is empty, don't save the value (field is being removed)
      if (isEmpty) {
        return
      }

      // Commit the field value
      const newFields = [...issueFields]
      if (pendingSelectedField.name && !newFields.some(f => f.field.name === pendingSelectedField.name)) {
        newFields.push(toIssueFieldWithUnsavedValue(pendingSelectedField, fieldValue))
      }
      updateIssueFields(newFields)
    },
    [issueFields, pendingSelectedField, updateIssueFields],
  )

  const onIssueFieldPickerSelectionChange = useCallback((fields: IssueFieldPickerField[]) => {
    const field = fields[0]
    if (field?.id && field?.name && field?.dataType) {
      setPendingSelectedField({
        id: field.id,
        name: field.name,
        dataType: toIssueFieldType(field.dataType),
      })
    }
  }, [])

  const onIssueFieldValueChanged = useCallback(
    (field: IssueFieldWithUnsavedValue, value: IssueFieldSelectionValues) => {
      if (
        selectedPinnedFields.find(f => f.id === field.field.id) &&
        !issueFields.find(f => f.field.id === field.field.id)
      ) {
        updateIssueFields([...issueFields, toIssueFieldWithUnsavedValue(field.field, value)])
        return
      }
      updateIssueFields(
        issueFields
          .map(f => {
            if (f.field.id === field.field.id) {
              return toIssueFieldWithUnsavedValue(f.field, value)
            }
            return f
          })
          .filter((f): f is IssueFieldWithUnsavedValue => f !== null), // Type guard to remove nulls
      )
    },
    [issueFields, selectedPinnedFields, updateIssueFields],
  )

  const pinnedFields = getPinnedFields(issueFields, selectedPinnedFields, scopedFields)
  const unpinnedFields = getUnpinnedFields(issueFields, selectedPinnedFields, scopedFields)

  const selectedFieldNames: string[] = pinnedFields
    .map(f => f.field.name)
    .concat(unpinnedFields.map(f => f.field.name))
    .filter((n): n is string => n !== undefined)

  return {
    pendingSelectedField,
    setPendingSelectedField,
    pinnedFields,
    unpinnedFields,
    selectedFieldNames,
    onPendingFieldCommit,
    onIssueFieldPickerSelectionChange,
    onIssueFieldValueChanged,
  }
}

const getUnpinnedFields = (
  fields: IssueFieldWithUnsavedValue[],
  pinnedFieldsFromType: IssueFieldSelection[],
  scopedFields: ScopedField[],
): IssueFieldWithUnsavedValue[] => {
  const scopedFieldIds = scopedFields?.map(sf => sf.fieldId) || []
  const fieldIDs = fields.map(f => f.field.id).filter(id => id !== undefined)
  const allFieldIds = [...new Set([...fieldIDs, ...scopedFieldIds])]
  const unpinnedFields = []

  for (const fieldId of allFieldIds) {
    if (!pinnedFieldsFromType.find(f => f.id === fieldId)) {
      const scopedField = scopedFields?.find(sf => sf.fieldId === fieldId)
      // A match means this is a field that was selected by the user
      const existingField = fields.find(f => f.field.id === fieldId)

      unpinnedFields.push(
        existingField
          ? existingField
          : toIssueFieldWithValue(
              // If existingField is not found, it must be a scoped field
              {id: fieldId, name: scopedField!.fieldName, dataType: scopedField!.fieldType},
              scopedField!.value,
              true,
            ),
      )
    }
  }
  return unpinnedFields
}

const getPinnedFields = (
  fields: IssueFieldWithUnsavedValue[],
  pinnedFieldsFromType: IssueFieldSelection[],
  scopedFields: ScopedField[],
): IssueFieldWithUnsavedValue[] => {
  const pinnedFields = []
  for (const field of pinnedFieldsFromType) {
    const match = fields.find(f => f.field.id === field.id)
    if (match) {
      // A match means this is a field that was selected by the user
      pinnedFields.push(match)
    } else {
      // If a scoped field exists for this pinned field, use its value and mark as readonly
      const scopedField = scopedFields.find(sf => sf.fieldId === field.id)
      pinnedFields.push(toIssueFieldWithValue(field, scopedField ? scopedField.value : null, !!scopedField))
    }
  }
  return pinnedFields
}
